/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { AlertsAction, AlertsActionType } from '../actions/alertsActions';
import { AlertsListDto, AlertsSearchParams, AlertDto, AlertsSortType } from '../api/alerts/alertsClient.typings';

export interface AlertsState {
    alertsList: AlertsListDto;
    animateAfterId: AlertDto['id'];
    newAlertsInfo: {
        count: number;
        dateFrom?: number;
        afterId?: number;
    };
    sort: AlertsSortType;
    filterParams: Pick<AlertsSearchParams, 'status' | 'assigneeUserId' | 'priority' | 'type'>;
    isLoading: boolean;
    selectedAlerts: AlertDto[];
    page: number;
    pageSize: number;
}

const initialState: AlertsState = {
    alertsList: {
        count: 0,
        alerts: [],
    },
    animateAfterId: null,
    newAlertsInfo: {
        count: 0,
        dateFrom: null,
        afterId: null,
    },
    sort: 'inserted,desc',
    filterParams: {
        status: ['IN_PROGRESS', 'RESOLVED', 'OPEN'],
        assigneeUserId: [],
        priority: [],
        type: [],
    },
    selectedAlerts: [],
    isLoading: false,
    pageSize: 20,
    page: 0,
};

export function alertsReducer(state: AlertsState = initialState, action: AlertsAction): AlertsState {
    switch (action.type) {
        case AlertsActionType.OPEN_ALERTS_PAGE:
            return {
                ...state,
                isLoading: true,
            };

        case AlertsActionType.CHANGE_ALERTS_PAGE:
            return {
                ...state,
                ...action.payload,
                isLoading: true,
            };

        case AlertsActionType.FETCH_ALERTS_PAGE_SUCCESS:
            const newState: AlertsState = {
                ...state,
                isLoading: false,
                animateAfterId: Infinity,
                alertsList: action.payload.list,
            };

            if (!(action.payload.list.alerts.length && state.newAlertsInfo.count)) {
                return newState;
            }

            const newAlertsList = action.payload.list.alerts.slice();

            newAlertsList.sort((a, b) => a.id - b.id);

            const maxId = Math.max(newAlertsList[0].id, newAlertsList[action.payload.list.alerts.length - 1].id);

            const currentAfterId = state.newAlertsInfo.afterId;

            if (maxId > currentAfterId) {
                newState.newAlertsInfo = {
                    afterId: maxId,
                    dateFrom: newAlertsList[action.payload.list.alerts.length - 1].inserted,
                    count: 0,
                };
                newState.animateAfterId = currentAfterId;
            }

            return newState;

        case AlertsActionType.SET_BASE_TIME_TO_POLL_NEW_ALERTS:
            return {
                ...state,
                newAlertsInfo: {
                    count: 0,
                    dateFrom: action.payload.dateFrom,
                    afterId: action.payload.alertId,
                },
            };

        case AlertsActionType.FETCH_NEW_ALERTS_SUCCESS:
            return {
                ...state,
                newAlertsInfo: {
                    ...state.newAlertsInfo,
                    count: action.payload.count,
                },
            };

        case AlertsActionType.SHOW_NEW_ALERTS:
            return {
                ...state,
                isLoading: true,
                page: 0,
                sort: 'inserted,desc',
            };

        case AlertsActionType.CHANGE_FILTERS:
            return {
                ...state,
                page: 0,
                filterParams: action.payload,
                isLoading: true,
                selectedAlerts: [],
            };

        case AlertsActionType.CHANGE_SORT:
            return {
                ...state,
                page: 0,
                sort: action.payload,
                isLoading: true,
            };

        case AlertsActionType.SELECT_ALERTS:
            return {
                ...state,
                selectedAlerts: action.payload,
            };

        case AlertsActionType.UPDATE_ALERTS:
            return {
                ...state,
                isLoading: true,
            };

        case AlertsActionType.UPDATE_ALERTS_SUCCESS:
            const newAlerts = state.alertsList.alerts;

            for (let i = 0; i < action.payload.length; i++) {
                const index = state.alertsList.alerts.findIndex((item) => item.id === action.payload[i].id);

                if (index >= 0) {
                    newAlerts.splice(index, 1, action.payload[i]);
                }
            }

            return {
                ...state,
                isLoading: false,
                alertsList: {
                    ...state.alertsList,
                    alerts: newAlerts,
                },
            };

        case AlertsActionType.FETCH_ALERTS_PAGE_ERROR:
            return {
                ...state,
                alertsList: {
                    count: 0,
                    alerts: [],
                },
                isLoading: false,
            };

        case AlertsActionType.EXIT_ALERTS_PAGE:
            return {
                ...state,
                alertsList: {
                    count: 0,
                    alerts: [],
                },
                animateAfterId: null,
                newAlertsInfo: {
                    count: 0,
                    dateFrom: null,
                    afterId: null,
                },
                isLoading: false,
            };
    }
    return state;
}
